import { type AxisConfig, type ScaleName } from '../../../../models';
import {
  type ChartsAxisProps,
  isBandScaleConfig,
  isPointScaleConfig,
  type ChartsRotationAxisProps,
  type ChartsRadiusAxisProps,
  type PolarAxisDefaultized,
  type AxisId,
  type PolarAxisConfig,
  isContinuousScaleConfig,
} from '../../../../models/axis';
import {
  type ChartSeriesType,
  type PolarChartSeriesType,
} from '../../../../models/seriesType/config';
import { getColorScale, getOrdinalColorScale } from '../../../colorScale';
import { getDefaultTickNumber, getTickNumber, scaleTickNumberByRange } from '../../../ticks';
import { getScale } from '../../../getScale';
import { isDateData, createDateFormatter } from '../../../dateHelpers';
import { getAxisExtremum } from './getAxisExtremum';
import type { ChartDrawingArea } from '../../../../hooks';
import { type ChartSeriesConfig } from '../../corePlugins/useChartSeriesConfig';
import { type ProcessedSeries } from '../../corePlugins/useChartSeries/useChartSeries.types';
import { deg2rad } from '../../../angleConversion';
import { getAxisTriggerTooltip } from './getAxisTriggerTooltip';
import { scaleBand, scalePoint } from '../../../scales';

export type DefaultizedAxisConfig<
  AxisProps extends ChartsRotationAxisProps | ChartsRadiusAxisProps,
> = {
  [axisId: AxisId]: PolarAxisDefaultized<ScaleName, any, AxisProps>;
};

type RotationConfig = PolarAxisConfig<ScaleName, any, ChartsRotationAxisProps>;

function getRange(
  drawingArea: ChartDrawingArea,
  axisDirection: 'rotation' | 'radius',
  axis: PolarAxisConfig<ScaleName, any>,
) {
  if (axisDirection === 'rotation') {
    if (axis.scaleType === 'point') {
      const angles = [
        deg2rad((axis as RotationConfig).startAngle, 0),
        deg2rad((axis as RotationConfig).endAngle, 2 * Math.PI),
      ];
      const diff = angles[1] - angles[0];
      if (diff > Math.PI * 2 - 0.1) {
        // If we cover a full circle, we remove a slice to avoid having data point at the same place.
        angles[1] -= diff / axis.data!.length;
      }
      return angles;
    }
    return [
      deg2rad((axis as RotationConfig).startAngle, 0),
      deg2rad((axis as RotationConfig).endAngle, 2 * Math.PI),
    ];
  }
  return [0, Math.min(drawingArea.height, drawingArea.width) / 2];
}

const DEFAULT_CATEGORY_GAP_RATIO = 0.2;
const DEFAULT_BAR_GAP_RATIO = 0.1;

export type ComputeResult<T extends ChartsAxisProps> = {
  axis: DefaultizedAxisConfig<T>;
  axisIds: string[];
};

type ComputeCommonParams<SeriesType extends ChartSeriesType = ChartSeriesType> = {
  drawingArea: ChartDrawingArea;
  formattedSeries: ProcessedSeries<SeriesType>;
  seriesConfig: ChartSeriesConfig<SeriesType>;
};

export function computeAxisValue<SeriesType extends ChartSeriesType>(
  options: ComputeCommonParams<SeriesType> & {
    axis?: AxisConfig<'linear', any, ChartsRadiusAxisProps>[];
    axisDirection: 'radius';
  },
): ComputeResult<ChartsRadiusAxisProps>;
export function computeAxisValue<SeriesType extends ChartSeriesType>(
  options: ComputeCommonParams<SeriesType> & {
    axis?: AxisConfig<ScaleName, any, ChartsRotationAxisProps>[];
    axisDirection: 'rotation';
  },
): ComputeResult<ChartsRotationAxisProps>;
export function computeAxisValue<SeriesType extends ChartSeriesType>({
  drawingArea,
  formattedSeries,
  axis: allAxis,
  seriesConfig,
  axisDirection,
}: ComputeCommonParams<SeriesType> & {
  axis?: AxisConfig<ScaleName, any, ChartsAxisProps>[];
  axisDirection: 'radius' | 'rotation';
}) {
  if (allAxis === undefined) {
    return {
      axis: {},
      axisIds: [],
    };
  }

  const axisIdsTriggeringTooltip = getAxisTriggerTooltip(
    axisDirection,
    seriesConfig as ChartSeriesConfig<PolarChartSeriesType>,
    formattedSeries,
    allAxis[0].id,
  );

  const completeAxis: DefaultizedAxisConfig<ChartsAxisProps> = {};
  allAxis.forEach((eachAxis, axisIndex) => {
    const axis = eachAxis as Readonly<AxisConfig<ScaleName, any, Readonly<ChartsAxisProps>>>;
    const range = getRange(drawingArea, axisDirection, axis);

    const [minData, maxData] = getAxisExtremum(
      axis,
      axisDirection,
      seriesConfig as ChartSeriesConfig<PolarChartSeriesType>,
      axisIndex,
      formattedSeries,
    );

    const triggerTooltip = !axis.ignoreTooltip && axisIdsTriggeringTooltip.has(axis.id);

    const data = axis.data ?? [];

    if (isBandScaleConfig(axis)) {
      const categoryGapRatio = axis.categoryGapRatio ?? DEFAULT_CATEGORY_GAP_RATIO;
      const barGapRatio = axis.barGapRatio ?? DEFAULT_BAR_GAP_RATIO;

      completeAxis[axis.id] = {
        offset: 0,
        categoryGapRatio,
        barGapRatio,
        triggerTooltip,
        ...axis,
        data,
        scale: scaleBand(axis.data!, range)
          .paddingInner(categoryGapRatio)
          .paddingOuter(categoryGapRatio / 2),
        tickNumber: axis.data!.length,
        colorScale:
          axis.colorMap &&
          (axis.colorMap.type === 'ordinal'
            ? getOrdinalColorScale({ values: axis.data, ...axis.colorMap })
            : getColorScale(axis.colorMap)),
      };

      if (isDateData(axis.data)) {
        const dateFormatter = createDateFormatter(axis.data, range, axis.tickNumber);
        completeAxis[axis.id].valueFormatter = axis.valueFormatter ?? dateFormatter;
      }
    }
    if (isPointScaleConfig(axis)) {
      completeAxis[axis.id] = {
        offset: 0,
        triggerTooltip,
        ...axis,
        data,
        scale: scalePoint(axis.data!, range),
        tickNumber: axis.data!.length,
        colorScale:
          axis.colorMap &&
          (axis.colorMap.type === 'ordinal'
            ? getOrdinalColorScale({ values: axis.data, ...axis.colorMap })
            : getColorScale(axis.colorMap)),
      };

      if (isDateData(axis.data)) {
        const dateFormatter = createDateFormatter(axis.data, range, axis.tickNumber);
        completeAxis[axis.id].valueFormatter = axis.valueFormatter ?? dateFormatter;
      }
    }

    if (!isContinuousScaleConfig(axis)) {
      // Could be merged with the two previous "if conditions" but then TS does not get that `axis.scaleType` can't be `band` or `point`.
      return;
    }

    const scaleType = axis.scaleType ?? ('linear' as const);

    const domainLimit = axis.domainLimit ?? 'nice';

    const axisExtremums = [axis.min ?? minData, axis.max ?? maxData];

    if (typeof domainLimit === 'function') {
      const { min, max } = domainLimit(minData, maxData);
      axisExtremums[0] = min;
      axisExtremums[1] = max;
    }

    const rawTickNumber = getTickNumber(
      axis,
      axisExtremums,
      getDefaultTickNumber(Math.abs(range[1] - range[0])),
    );
    const tickNumber = scaleTickNumberByRange(rawTickNumber, range);

    const scale = getScale(scaleType, axisExtremums, range);
    const finalScale = domainLimit === 'nice' ? scale.nice(rawTickNumber) : scale;
    const [minDomain, maxDomain] = finalScale.domain();
    const domain = [axis.min ?? minDomain, axis.max ?? maxDomain];

    completeAxis[axis.id] = {
      offset: 0,
      triggerTooltip,
      ...axis,
      data,
      scaleType: scaleType as any,
      scale: finalScale.domain(domain) as any,
      tickNumber,
      colorScale: axis.colorMap && getColorScale(axis.colorMap),
    };
  });
  return {
    axis: completeAxis,
    axisIds: allAxis.map(({ id }) => id),
  };
}
